
import React, { useState, useEffect } from 'react';
import { Menu, X, Cloud, Globe } from 'lucide-react';
import { Language, Translation } from '../types';

interface NavbarProps {
  lang: Language;
  setLang: (l: Language) => void;
  t: Translation['nav'];
}

export const Navbar: React.FC<NavbarProps> = ({ lang, setLang, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t.home, href: '#' },
    { name: t.products, href: '#products' },
    { name: t.features, href: '#features' },
    { name: t.contact, href: '#contact' },
  ];

  const toggleLang = () => setLang(lang === 'en' ? 'cn' : 'en');

  return (
    <header 
      className={`fixed top-0 w-full z-40 transition-all duration-300 ${
        scrolled ? 'bg-slate-900/90 backdrop-blur-md shadow-lg py-3' : 'bg-transparent py-5'
      }`}
    >
      <nav className="container mx-auto px-4 flex justify-between items-center" aria-label="Main Navigation">
        {/* Logo */}
        <a href="#" className="flex items-center gap-2 text-2xl font-bold text-white tracking-tight" aria-label="CloudResell Pro Home">
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-lg">
            <Cloud className="text-white" size={24} />
          </div>
          <span>Cloud<span className="text-blue-400">Resell</span></span>
        </a>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a 
              key={link.name} 
              href={link.href}
              className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
            >
              {link.name}
            </a>
          ))}
          
          <button 
            onClick={toggleLang}
            aria-label={`Switch language to ${lang === 'en' ? 'Chinese' : 'English'}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-800 text-slate-300 hover:text-white hover:border-slate-500 transition-all text-xs font-semibold"
          >
            <Globe size={14} />
            {lang === 'en' ? 'CN' : 'EN'}
          </button>

          <a 
            href="https://t.me/ksiuiwi552" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-white text-slate-900 hover:bg-blue-50 px-5 py-2 rounded-full font-bold text-sm transition-transform hover:scale-105"
          >
            Telegram
          </a>
        </div>

        {/* Mobile Toggle */}
        <div className="md:hidden flex items-center gap-4">
          <button 
            onClick={toggleLang} 
            className="text-slate-300 hover:text-white"
            aria-label="Toggle Language"
          >
            <span className="font-bold">{lang.toUpperCase()}</span>
          </button>
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="text-white"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-slate-900 border-t border-slate-800 p-4 flex flex-col gap-4 shadow-xl">
          {navLinks.map((link) => (
            <a 
              key={link.name} 
              href={link.href}
              onClick={() => setIsOpen(false)}
              className="block text-slate-300 hover:text-white text-lg font-medium py-2"
            >
              {link.name}
            </a>
          ))}
          <a 
            href="https://t.me/ksiuiwi552"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center bg-blue-600 text-white py-3 rounded-lg font-bold"
          >
            Telegram
          </a>
        </div>
      )}
    </header>
  );
};

